import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, X, Undo2, AlertTriangle } from 'lucide-react';
import { api } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';

/**
 * Ask a courier to collect a delivered parcel back from the customer.
 *
 * The screen says out loud what the operator is about to cause, because a return is NOT an edit to
 * the shipment on screen: it books a NEW consignment, with a new waybill, that somebody has to pay
 * for. The delivered order is left exactly as it is — its delivery already happened and the record
 * of it must survive.
 *
 * The courier list comes from the server (`/orders/return-couriers`) rather than the full courier
 * master: only a service configured for reverse pickup can do this, so offering any other one would
 * be offering a booking that is refused a second later.
 */
export function InitiateReturnDialog({ open, order, onClose, onDone }: {
  open: boolean;
  order: { id: string; orderNo: string; awbNo?: string | null; customerName?: string; customerCity?: string; deliveryType?: string } | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const isMerchant = useAuth((s) => s.user)?.kind === 'merchant';
  const [courierId, setCourierId] = useState<number | ''>('');
  const [remarks, setRemarks] = useState('');

  const { data: couriers, isLoading: loadingCouriers } = useQuery({
    queryKey: ['return-couriers'],
    queryFn: async () => (await api.get('/orders/return-couriers')).data as Array<{ id: number; name: string; mode: string }>,
    enabled: open,
  });

  // Pre-select when there is only one — with a single reverse service, asking is just a click.
  useEffect(() => {
    if (!open) { setRemarks(''); setCourierId(''); return; }
    if (couriers?.length === 1) setCourierId(couriers[0].id);
  }, [open, couriers]);

  const m = useMutation({
    mutationFn: async () =>
      (await api.post(`/orders/${order!.id}/initiate-return`, {
        courierId: courierId || undefined,
        remarks: remarks.trim() || undefined,
      })).data,
    onSuccess: (r: any) => {
      // A return order that was CREATED but not booked is not a success — say which happened.
      if (r?.ok) toast.success(r.message ?? 'Return booked');
      else toast.warning(r?.message ?? 'Return order created but not booked');
      onDone();
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not start the return'),
  });

  if (!open || !order) return null;
  const noCouriers = !loadingCouriers && !(couriers ?? []).length;

  return (
    <div className="fixed inset-0 z-[1100] bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div>
            <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Undo2 size={16} className="text-amber-600" /> Initiate return</h3>
            <p className="text-xs text-slate-500">Collect this delivered parcel back from the customer</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs">
            <div className="text-slate-500">Delivered shipment</div>
            <div className="font-mono text-slate-900">{order.awbNo ?? order.orderNo}</div>
            <div className="text-slate-600 mt-0.5">
              {order.customerName}{order.customerCity ? ` — ${order.customerCity}` : ''}
            </div>
          </div>

          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-900 flex gap-2">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <div>
              This books a <b>new shipment</b> with its own waybill, collected from the customer and
              returned to your pickup warehouse. The delivered order is not changed. The return is always
              <b> Prepaid</b> — nothing is collected from the customer{order.deliveryType === 'COD' ? ', even though the original was COD' : ''}.
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Collected by *</label>
            {loadingCouriers ? (
              <div className="text-xs text-slate-500 flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Loading couriers…</div>
            ) : noCouriers ? (
              <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                {isMerchant ? (
                  <>Reverse pickup is not available on your account. Please contact our team.</>
                ) : (
                  <>No courier is switched on for reverse pickup. Turn on <b>Reverse Pickup</b> for a Delhivery B2C
                  courier under Masters → Couriers. Every merchant can then use it, unless it is switched off for
                  them under Merchant → Couriers.</>
                )}
              </div>
            ) : (
              <select value={courierId} onChange={(e) => setCourierId(e.target.value ? Number(e.target.value) : '')} className="input w-full">
                <option value="">Choose a reverse-pickup service…</option>
                {(couriers ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Reason (optional)</label>
            <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} maxLength={2000} rows={3}
              className="input w-full resize-none" placeholder="e.g. customer reported a wrong size" />
            <div className="text-[11px] text-slate-400 mt-0.5">Saved on both shipments and in the edit history.</div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50">Cancel</button>
          <button onClick={() => m.mutate()} disabled={!courierId || m.isPending}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
            {m.isPending ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />} Book reverse pickup
          </button>
        </div>
      </div>
    </div>
  );
}
